import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Container } from "@maeiz/ui";

const ErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
    const { t } = useTranslation();
    return (
        <Container>
            <Card
                padding="lg"
                className="mx-auto mt-10 max-w-lg space-y-4 text-center"
                role="alert"
            >
                <h1 className="font-display text-xl font-semibold text-neutral-dark dark:text-white">
                    {t("errors.crash.title")}
                </h1>
                <p className="text-sm text-neutral-secondary">{t("errors.crash.body")}</p>
                <div className="flex flex-wrap justify-center gap-2">
                    <Button type="button" variant="primary" onClick={onRetry}>
                        {t("errors.crash.retry")}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => window.location.assign("/")}
                    >
                        {t("errors.crash.home")}
                    </Button>
                </div>
            </Card>
        </Container>
    );
};

interface State {
    failed: boolean;
}

/** Catches errors thrown while rendering so a bug in one screen doesn't blank the whole console. */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
    state: State = { failed: false };

    static getDerivedStateFromError(): State {
        return { failed: true };
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo) {
        console.error("[ErrorBoundary]", error, info.componentStack);
    }

    render() {
        if (this.state.failed) {
            return <ErrorFallback onRetry={() => this.setState({ failed: false })} />;
        }
        return this.props.children;
    }
}
